import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { PRIVACY_POLICY, TERMS_OF_USE } from '@/legal/documents';
import { readLastStudioSignIn } from '@/lib/studioIdentity';

export function SignInPage() {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const lastSignIn = useMemo(() => readLastStudioSignIn(), []);
  const [email, setEmail] = useState(lastSignIn?.email ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const signIn = (opts?: { loginHint?: string }) => {
    if (submitting) return;
    setSubmitting(true);
    setFailed(false);
    void loginWithRedirect({
      authorizationParams: {
        ...(opts?.loginHint ? { login_hint: opts.loginHint } : {}),
      },
    }).catch(() => {
      setFailed(true);
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <main className="flex min-h-dvh w-full items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-3 text-foreground">
          <RapidSOSMark className="h-6 w-6" animation={DEFAULT_MARK_ANIMATION} />
          <RapidSOSWordmark className="h-6" />
          <span className="h-5 border-l border-border" aria-hidden="true" />
          <span className="text-sm font-medium text-muted-foreground">Studio</span>
        </div>

        <section className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8" aria-labelledby="studio-sign-in-title">
          <h1 id="studio-sign-in-title" className="text-2xl font-semibold tracking-tight text-foreground">
            {t('auth.signInTitle')}
          </h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{t('auth.ssoSubtitle')}</p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              signIn({ loginHint: email.trim() || undefined });
            }}
            className="mt-6 space-y-4"
          >
            <div>
              <Label htmlFor="studio-email" className="text-sm font-medium">{t('auth.workEmail')}</Label>
              <Input
                id="studio-email"
                type="email"
                autoComplete="email"
                required
                disabled={submitting}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="mt-2 h-11"
              />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={submitting} aria-busy={submitting}>
              {submitting ? t('auth.connecting') : t('auth.continueWithSSO')}
            </Button>
            {failed ? <p role="alert" className="text-sm text-destructive">{t('auth.errorBody')}</p> : null}
          </form>
          <p className="mt-4 text-center text-xs leading-5 text-muted-foreground">{t('auth.ssoRedirect')}</p>
        </section>

        <p className="mt-6 text-center text-xs leading-5 text-muted-foreground">{t('auth.accessManaged')}</p>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          <Trans
            i18nKey="legal.signInNotice"
            components={{
              terms: <Link to={TERMS_OF_USE.path} className="underline underline-offset-2 hover:text-foreground" />,
              privacy: <Link to={PRIVACY_POLICY.path} className="underline underline-offset-2 hover:text-foreground" />,
            }}
          />
        </p>
      </div>
    </main>
  );
}
